import { getRouteApi, useNavigate } from '@tanstack/react-router';

import { FormattedMessage, useIntl } from 'react-intl';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import BackIcon from '@mui/icons-material/ArrowBack';
import Toolbar from '@mui/material/Toolbar';
import Spacings from '~/modules/ui-kit/spacings';
import ContentView from '~/components/ContentView';
import CongregationForm from '~/components/CongregationForm';
import CongregationDeleteButton from '~/components/CongregationDeleteButton';
import { useCongregation } from '~/hooks/api/useCongregation';
import { useCircuits } from '~/hooks/api/useCircuits';
import { useEditCongregation } from '~/hooks/api/useEditCongregation';
import { UpdateCongregation } from '~/generated';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import messages from './messages';
import styles from './styles.module.css';

const route = getRouteApi('/congregations/$congregationId');

export const CongregationEdit = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const search = route.useSearch();
  const { congregationId } = route.useParams();
  const congregationIdNum = parseInt(congregationId, 10);
  const { data: congregation, isLoading: isLoadingCongregation } =
    useCongregation(congregationIdNum);
  const { data: circuits, isLoading: isLoadingCircuits } = useCircuits();
  const handleBackButton = () => {
    navigate({ to: search.from || '/congregations' });
  };
  const editCongregationMutation = useEditCongregation(congregationIdNum, {
    onSuccess() {
      showSnackbar({
        message: intl.formatMessage(messages.saveSuccess),
      });
      // we redirect the user back to the congregation list page
      handleBackButton();
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.saveFailure),
      });
    },
  });
  const isFetching = isLoadingCongregation || isLoadingCircuits;
  const isLoading = editCongregationMutation.isPending || isFetching;
  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar className={styles.header}>
            <Spacings.Inline alignItems="center">
              <IconButton
                color="inherit"
                onClick={handleBackButton}
                size="large"
              >
                <BackIcon data-testid="back" />
              </IconButton>
              <div>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'inherit',
                  }}
                >
                  <FormattedMessage {...messages.title} />
                </Typography>
              </div>
            </Spacings.Inline>
            <div>
              <CongregationDeleteButton
                congregationId={congregationIdNum}
                handleBackButton={handleBackButton}
              />
            </div>
          </Toolbar>
        </AppBar>
      }
      isLoading={isLoading}
    >
      {!isFetching && circuits && congregation && (
        <CongregationForm
          onSubmit={(values) =>
            editCongregationMutation.mutate({
              id: congregationIdNum,
              name: values.name,
              circuitId: values.circuit,
              number: values.watchtowerId || null,
            } as UpdateCongregation)
          }
          circuits={circuits}
          initialValues={{
            circuit: congregation.circuitId || undefined,
            name: congregation.name || undefined,
            watchtowerId: congregation.number || undefined,
          }}
        />
      )}
    </ContentView>
  );
};

export default CongregationEdit;
